import { useQuery } from '@tanstack/react-query'
import { toast } from '@/design-system'
import { getStreak } from '@/services/api/social'
import { useSession } from '@/stores/session'

const MILESTONES = [3, 7, 14, 30, 100]

/** Celebrate each milestone exactly once per device. */
function celebrate(days: number) {
  try {
    const seen = Number(localStorage.getItem('catgo.streak-milestone') ?? 0)
    if (days > seen && MILESTONES.includes(days)) {
      localStorage.setItem('catgo.streak-milestone', String(days))
      toast.success(
        days === 3
          ? '🐾 ¡3 días seguidos!'
          : days === 7
            ? '🐾 ¡Una semana entera!'
            : '🐾 ¡' + days + ' días seguidos!',
      )
    }
  } catch {
    // A device that blocks storage just celebrates more often. Fine.
  }
}

/**
 * The soft streak, worn next to the greeting: alive, waiting, or shining on
 * a milestone day. Pays nothing but pride. (§0047)
 */
export function StreakChip() {
  const user = useSession((s) => s.user)
  const streak = useQuery({
    queryKey: ['streak', user?.id],
    queryFn: getStreak,
    enabled: Boolean(user),
    staleTime: 60_000,
  })

  const s = streak.data
  if (!s) return null

  if (s.days === 0) {
    if (s.rested_from != null) {
      return (
        <span className="streak-chip streak-chip--dim" title="La racha descansa">
          🐾 La racha descansa. Los gatos siguen ahí.
        </span>
      )
    }
    return null
  }

  const milestoneToday = s.today_claimed && MILESTONES.includes(s.days)
  if (milestoneToday) celebrate(s.days)

  return (
    <span
      className={
        'streak-chip' +
        (milestoneToday ? ' streak-chip--milestone' : s.today_claimed ? '' : ' streak-chip--dim')
      }
      title={s.today_claimed ? 'Racha de ' + s.days + ' días' : 'La racha te espera en Descubrir'}
    >
      🐾 {milestoneToday ? '¡' + s.days + ' días!' : s.days + (s.days === 1 ? ' día' : ' días')}
      {!s.today_claimed && <span className="streak-chip__hint"> · hoy aún nada</span>}
    </span>
  )
}
